import React, { useMemo } from 'react'

// Shared intensity ramp for every activity heatmap (per-feature body and
// project-level calendar). Index 0 is "no activity"; 1..4 are the honey ramp
// from lightest to darkest. Centralized here so a color tweak ripples
// everywhere instead of needing a sweep across calendar consumers.
const INTENSITY_CLASSES = [
  'bg-stone-200',
  'bg-amber-200',
  'bg-amber-300',
  'bg-amber-500',
  'bg-amber-600',
] as const

// Map an activity count into one of the ramp classes given the
// max-in-window. Same bucketing as before: ceil(count/max * 4).
export function intensityClassFor(count: number, max: number): string {
  if (count === 0) return INTENSITY_CLASSES[0]
  const idx = Math.min(4, Math.ceil((count / Math.max(1, max)) * 4))
  return INTENSITY_CLASSES[idx]
}

// --- Honeycomb geometry -----------------------------------------------------
//
// The cells tessellate as a comb rather than sitting in a plain grid, which
// means every other line is offset by half a cell. Which line gets offset is
// forced by the calendar: weeks run down the page and nobody reads across
// them, but the seven columns are weekdays, they carry a header, and a Tuesday
// has to stay above a Tuesday.
//
// So the hexagons are FLAT-TOP. Flat-top is the orientation that tessellates by
// offsetting columns: the seven columns keep their x positions and each one
// simply sits half a row lower than its neighbours, leaving the zigzag on the
// week axis where it costs nothing. (Pointy-top hexes tessellate by offsetting
// rows, which would shear the weekday header off its columns.)
//
// Consequence of the packing: neighbouring columns overlap by a quarter of a
// cell, so a month block comes out *narrower* than the old square grid (105px
// vs 115px) despite the larger cells.
const HEX_WIDTH = 18 // corner-to-corner, horizontal
const HEX_HEIGHT = (HEX_WIDTH * Math.sqrt(3)) / 2 // flat-to-flat, vertical
const HEX_WALL = 1 // gap between neighbouring cells
const HEX_COL_PITCH = HEX_WIDTH * 0.75 + HEX_WALL
const HEX_ROW_PITCH = HEX_HEIGHT + HEX_WALL
const HEX_COL_SHIFT = HEX_ROW_PITCH / 2 // every other column drops half a row
const HEX_CLIP = 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
const HEX_RIM = 2 // white rim revealed around a hovered cell

const ROWS = 6 // the most week rows any month can span
const GRID_WIDTH = HEX_COL_PITCH * 6 + HEX_WIDTH
const GRID_HEIGHT = HEX_ROW_PITCH * (ROWS - 1) + HEX_COL_SHIFT + HEX_HEIGHT
const HEADER_HEIGHT = 12

interface HexCellProps {
  /** Tailwind background class, for consumers on the intensity ramp. */
  colorClass?: string
  /** Literal background color, for consumers with their own palette. */
  color?: string
  title?: string
  /** Test hooks: the cell's day (YYYY-MM-DD) and what it shows, as
   *  `data-day` / `data-state`, for consumers whose cells carry a state. */
  day?: string
  state?: string
}

/** One comb cell, absolutely positioned to fill the grid slot it's given.
 *  Shared by every heatmap consumer so the shape and the hover can't drift
 *  between them.
 *
 *  Hovering pops the cell up out of the comb. Two things make that work in a
 *  tessellation, where a growing cell has no gap to grow into and just runs
 *  into its neighbours:
 *
 *  - The white rim is a second hexagon `HEX_RIM` pixels LARGER than the cell,
 *    not a margin carved out of it. Inset the fill instead and the inset scales
 *    along with everything else, so the colored area ends up no bigger than it
 *    started and only the rim appears to grow. The rim stays transparent until
 *    hover — painted at rest it would overlap the neighbours drawn before it
 *    and shave their edges.
 *  - `z-10` lifts the cell above the next column, which is painted after it and
 *    would otherwise crop the rim.
 *
 *  (`ring-1`, the old square grid's hover, is not an option: clip-path cuts the
 *  ring off at the corners.) */
export const HexCell: React.FC<HexCellProps> = ({ colorClass = '', color, title, day, state }) => (
  <div
    className="absolute transition duration-150 hover:z-10 hover:scale-125 hover:bg-white"
    style={{ clipPath: HEX_CLIP, inset: -HEX_RIM }}
    title={title}
    data-day={day}
    data-state={state}
  >
    <div
      className={`absolute ${colorClass}`}
      style={{ clipPath: HEX_CLIP, inset: HEX_RIM, ...(color ? { backgroundColor: color } : null) }}
    />
  </div>
)

interface ActivityCalendarProps {
  year: number
  month: number // 0-11 (JavaScript month format)
  activityData: Record<string, number> // date string (YYYY-MM-DD) -> activity count
  renderCell: (date: string, activityCount: number, dayNumber: number) => React.ReactNode
  className?: string
}

const ActivityCalendar: React.FC<ActivityCalendarProps> = ({
  year,
  month,
  activityData,
  renderCell,
  className = ''
}) => {
  const calendarDays = useMemo(() => {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const firstDayOfMonth = new Date(year, month, 1).getDay() // 0 = Sunday

    // Only the month's own days get a cell. The leading and trailing slots
    // belong to the neighbouring months and are simply left empty, so each
    // month reads as its own piece of comb instead of trailing a faded fringe
    // of days it doesn't own. `slot` is the position in the 6x7 grid, which is
    // what keeps every day under its weekday column.
    const days: Array<{ date: string; dayNumber: number; slot: number }> = []
    for (let day = 1; day <= daysInMonth; day++) {
      const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      days.push({ date, dayNumber: day, slot: firstDayOfMonth + day - 1 })
    }
    return days
  }, [year, month])

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className={`activity-calendar inline-block ${className}`}>
      {/* Week day headers - single letters, one per column. The columns are
          staggered but keep their x positions, so the header stays flat. */}
      <div className="relative mb-[3px]" style={{ width: GRID_WIDTH, height: HEADER_HEIGHT }}>
        {weekDays.map((day, col) => (
          <div
            key={day + col}
            className="absolute top-0 text-center text-[9px] font-medium text-gray-400"
            style={{ left: col * HEX_COL_PITCH, width: HEX_WIDTH }}
          >
            {day[0]}
          </div>
        ))}
      </div>

      {/* The comb. Cells are positioned rather than laid out on a grid because
          neighbouring columns overlap and every other one is shifted. */}
      <div className="relative" style={{ width: GRID_WIDTH, height: GRID_HEIGHT }}>
        {calendarDays.map(({ date, dayNumber, slot }) => {
          const col = slot % 7
          const row = Math.floor(slot / 7)
          const activityCount = activityData[date] || 0
          return (
            <div
              key={date}
              className="absolute"
              style={{
                left: col * HEX_COL_PITCH,
                top: row * HEX_ROW_PITCH + (col % 2 ? HEX_COL_SHIFT : 0),
                width: HEX_WIDTH,
                height: HEX_HEIGHT,
              }}
            >
              {renderCell(date, activityCount, dayNumber)}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ActivityCalendar
